import React from 'react';
import { Alert } from '@mui/material';
import type { CoverageGapSchema } from '@store/generated/patient-coverage';

const formatShortDate = (isoDate?: string | null): string | null => {
  if (!isoDate) return null;
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

interface CoverageGapNoticeProps {
  coverageInForceToday?: boolean | null;
  nextCoverageGap?: CoverageGapSchema | null;
}

/**
 * Patient-level billable-coverage gap banner (GDN-2411 / D5). Warns when no
 * plan is in force today, or when a gap is coming up between plans — visits
 * in the gap will have no payer and their claims will be flagged.
 */
export const CoverageGapNotice: React.FC<CoverageGapNoticeProps> = ({
  coverageInForceToday,
  nextCoverageGap,
}) => {
  if (!nextCoverageGap) {
    return null;
  }

  const start = formatShortDate(nextCoverageGap.start);
  const end = formatShortDate(nextCoverageGap.end);
  const range = end ? `${start} – ${end}` : `${start} onward`;

  if (coverageInForceToday === false) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No insurance coverage is in force today ({range}). Visits in this
        window have no billable plan — claims for them will be flagged.
      </Alert>
    );
  }

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      Upcoming coverage gap: {range}. Visits in this window will have no
      billable plan.
    </Alert>
  );
};
